import Link from "next/link";
import { useRouter } from "next/router";
import { FunctionComponent, useEffect, useState } from "react";
import { useUser } from "../../hooks/useUser";
import { betaSignUp, signIn, signUp } from "../../lib/auth-fetcher";
import { getErrorMessage } from "../../lib/form";
import Button from "../lib/Button/Button";
import { Heading } from "../lib/Text/Heading";
import TextField from "../lib/TextField/TextField";

type AuthFormProps = {
  isSignup?: boolean;
  isBetaSignup?: boolean;
};

const AuthForm: FunctionComponent<AuthFormProps> = ({
  isSignup,
  isBetaSignup,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [successMsg, setSuccessMsg] = useState<string>("");
  const { user, loading, loggedOut, error, mutate } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (user) {
      router.push("/admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  async function handleSubmit(event: any) {
    event.preventDefault();
    if (isLoading || loading) return;
    setIsLoading(true);
    const emailElement = event.currentTarget.elements.email;
    const passwordElement = event.currentTarget.elements.password;
    const invitationCode = event.currentTarget.elements.invitationCode;

    try {
      let res: { user: any } = { user: null };
      if (isSignup) {
        res = await signUp({
          email: emailElement.value,
          password: passwordElement.value,
          invitationCode: invitationCode.value,
        });
      } else if (isBetaSignup) {
        res = await betaSignUp(emailElement.value);
        setErrorMsg(undefined);
        setSuccessMsg(
          "Erfolgreich angemeldet. Du wirst sofort weitergeleitet!",
        );
        router.push("/forms/demo/oB2IwnXyeB");
      } else {
        res = await signIn({
          email: emailElement.value,
          password: passwordElement.value,
        });
      }
      if (res) {
        await mutate({ user: res.user });
      }
    } catch (error: any) {
      setErrorMsg(getErrorMessage(error));
      setSuccessMsg("");
      setIsLoading(false);
    }
  }

  let authText = "Anmelden";

  if (isSignup) authText = "Registrieren";
  else if (isBetaSignup) authText = "Beta-Registrierung";

  return (
    <div className="max-w-md w-full mx-auto shadow-md bg-white py-12 px-6 mt-4 rounded-xl sm:py-16 sm:px-12 sm:rounded-2xl sm:mt-16">
      <div className="flex justify-between mb-8">
        <div className="flex items-center">
          <img
            src="/ghostform-logo.png"
            className="h-6 w-6 rounded-full mr-2"
          />
          <p>Ghostform</p>
        </div>
        {isSignup || isBetaSignup ? null : (
          <Link href="/signup" legacyBehavior>
            <a className="text-right text-primary no-underline hover:underline">
              Kein Account? <br />
              Jetzt registrieren
            </a>
          </Link>
        )}
        {!isBetaSignup ? null : (
          <Link href="/signup" legacyBehavior>
            <a className="text-right text-primary no-underline hover:underline">
              Code erhalten? <br />
              Jetzt registrieren
            </a>
          </Link>
        )}
      </div>
      <Heading level={1} className="normal-case tracking-tight mb-8">
        {authText}
      </Heading>
      <form onSubmit={handleSubmit}>
        {errorMsg && <p className="text-red-500 mb-4">{errorMsg}</p>}
        {!!successMsg && <p className="text-green-500 mb-4">{successMsg}</p>}
        <div className="mb-4 sm:mb-6">
          <TextField
            name="email"
            type="email"
            autoComplete="email"
            required
            label="Email Adresse"
            placeholder="hello@ghostform.io"
            showLabel={true}
          />
        </div>
        {isBetaSignup ? null : (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="password"
              type="password"
              autoComplete="password"
              required
              label="Passwort"
              placeholder="passwort123"
              showLabel={true}
            />
          </div>
        )}
        {isSignup ? (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="invitationCode"
              type="text"
              required
              label="Einladungs-Code"
              placeholder="Deine Einladung"
              showLabel={true}
            />
          </div>
        ) : null}
        <Button
          variant="primary"
          type="submit"
          isLoading={isLoading || loading}
        >
          {authText}
        </Button>
      </form>
    </div>
  );
};

export default AuthForm;
